import React, { useState, useEffect, useContext, useCallback } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import axios from 'axios';
import { toast } from 'sonner';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  CalendarClock, BellRing, CheckCircle2, XCircle, FilePlus2, Building2, MapPin, UserCog, AlertTriangle,
} from 'lucide-react';

const fmt = (d) => (d ? (_fmtDate(d) || d) : '—');

const FollowUpsView = ({ onOpenWorkOrder, onCreateServiceRequest }) => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [status, setStatus] = useState('active');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadEdits, setLeadEdits] = useState({});
  const [dismissTarget, setDismissTarget] = useState(null);
  const [dismissReason, setDismissReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/follow-up-reminders?status=${status}`, authHeaders)
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error('Failed to load follow-up reminders'))
      .finally(() => setLoading(false));
  }, [status, token]);
  useEffect(() => { load(); }, [load]);

  const saveLead = async (r) => {
    const v = parseInt(leadEdits[r.id], 10);
    if (Number.isNaN(v) || v < 0) { toast.error('Enter a valid lead time (days)'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/follow-up-reminders/${r.id}/lead-time`, { lead_time_days: v }, authHeaders);
      toast.success('Lead time updated');
      setLeadEdits((e) => { const n = { ...e }; delete n[r.id]; return n; });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); } finally { setBusy(false); }
  };
  const markDone = async (r) => {
    setBusy(true);
    try { await axios.post(`${API}/follow-up-reminders/${r.id}/done`, {}, authHeaders); toast.success('Marked done'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); } finally { setBusy(false); }
  };
  const confirmDismiss = async () => {
    if (!dismissReason.trim()) { toast.error('A reason is required to dismiss'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/follow-up-reminders/${dismissTarget.id}/dismiss`, { reason: dismissReason.trim() }, authHeaders);
      toast.success('Reminder dismissed');
      setDismissTarget(null); setDismissReason(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not dismiss'); } finally { setBusy(false); }
  };

  return (
    <div data-testid="followups-view">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">Operations</div>
          <h1 className="text-3xl font-extrabold text-[#0E2A47]">Follow-Ups</h1>
          <p className="text-sm text-slate-500 mt-1">Reminders linked to completed work orders. No open work order is created automatically.</p>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1">
          {['active', 'done', 'dismissed'].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`followups-tab-${s}`}
              className={`rounded-md px-3 py-1.5 text-sm font-medium capitalize ${status === s ? 'bg-[#0E2A47] text-white' : 'text-slate-600 hover:bg-gray-50'}`}>{s}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="py-16 text-center text-sm text-slate-400">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 py-16 text-center">
          <CalendarClock className="mx-auto mb-2 text-slate-300" size={28} />
          <p className="text-sm text-slate-400">No {status} follow-up reminders.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} data-testid={`followup-row-${r.tfs_work_order_number}`}
              className={`rounded-xl border bg-white p-4 shadow-sm ${r.due ? 'border-amber-300 ring-1 ring-amber-100' : 'border-gray-200'}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={() => onOpenWorkOrder && onOpenWorkOrder(r.work_order_id)}
                      className="font-mono text-sm font-bold text-[#0E2A47] hover:underline" data-testid={`followup-open-wo-${r.tfs_work_order_number}`}>
                      {r.tfs_work_order_number}
                    </button>
                    {r.due && <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700"><BellRing size={11} /> Due</span>}
                    {r.overdue && <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700"><AlertTriangle size={11} /> Overdue</span>}
                    {r.status !== 'scheduled' && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold capitalize text-slate-600">{r.status}</span>}
                  </div>
                  <p className="mt-1.5 text-sm font-medium text-slate-800">{r.recommendation}</p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Building2 size={12} /> {r.customer_name || '—'}</span>
                    <span className="flex items-center gap-1"><MapPin size={12} /> {r.service_location || '—'}</span>
                    <span className="flex items-center gap-1 font-semibold text-[#0E2A47]"><UserCog size={12} /> {r.assigned_operations_manager_name || 'Unassigned OM'}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    <span className="text-slate-500">Follow-up: <span className="font-semibold text-slate-700">{fmt(r.follow_up_date)}</span></span>
                    {r.status === 'scheduled' && <span className="text-slate-500">Notify on: <span className="font-semibold text-slate-700">{fmt(r.remind_on)}</span></span>}
                    {r.status === 'dismissed' && r.dismiss_reason && <span className="text-slate-500">Reason: <span className="font-medium text-slate-700">{r.dismiss_reason}</span></span>}
                    {r.resolved_by && <span className="text-slate-500">By: <span className="font-medium text-slate-700">{r.resolved_by}</span></span>}
                  </div>
                </div>

                {r.status === 'scheduled' && (
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] text-slate-400">Lead (days)</span>
                      <Input type="number" min="0" className="h-8 w-16 text-sm"
                        value={leadEdits[r.id] ?? r.lead_time_days}
                        onChange={(e) => setLeadEdits((s) => ({ ...s, [r.id]: e.target.value }))}
                        data-testid={`followup-lead-input-${r.tfs_work_order_number}`} />
                      <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => saveLead(r)}
                        data-testid={`followup-lead-save-${r.tfs_work_order_number}`}>Save</Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => onCreateServiceRequest && onCreateServiceRequest(r)}
                        data-testid={`followup-create-sr-${r.tfs_work_order_number}`}><FilePlus2 size={14} className="mr-1" /> New Request</Button>
                      <Button size="sm" variant="outline" className="h-8 text-emerald-700" disabled={busy} onClick={() => markDone(r)}
                        data-testid={`followup-done-${r.tfs_work_order_number}`}><CheckCircle2 size={14} className="mr-1" /> Done</Button>
                      <Button size="sm" variant="outline" className="h-8 text-red-600" disabled={busy} onClick={() => { setDismissTarget(r); setDismissReason(''); }}
                        data-testid={`followup-dismiss-${r.tfs_work_order_number}`}><XCircle size={14} className="mr-1" /> Dismiss</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dismissTarget} onOpenChange={(o) => { if (!o) { setDismissTarget(null); setDismissReason(''); } }}>
        <DialogContent data-testid="followup-dismiss-dialog">
          <DialogHeader><DialogTitle>Dismiss follow-up reminder</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">A reason is required. The link to work order <span className="font-mono font-semibold">{dismissTarget?.tfs_work_order_number}</span> and its audit history are preserved.</p>
          <Textarea placeholder="Reason for dismissing…" value={dismissReason} onChange={(e) => setDismissReason(e.target.value)}
            data-testid="followup-dismiss-reason" rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDismissTarget(null); setDismissReason(''); }} disabled={busy}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={confirmDismiss} disabled={busy} data-testid="followup-dismiss-confirm">Dismiss</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FollowUpsView;
